import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from '@/contexts/useTour';
import { TourSpotlight } from './TourSpotlight';
import { TourTooltip } from './TourTooltip';

export function ProductTour() {
  const {
    currentTour,
    currentStep,
    isActive,
    nextStep,
    previousStep,
    skipTour,
    dismissTour,
  } = useTour();

  // Prevent body scroll when tour is active
  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isActive]);

  // Handle escape key to close tour
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isActive) {
        dismissTour();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isActive, dismissTour]);

  // Dismiss tour when clicking outside the tooltip and outside the highlighted element
  useEffect(() => {
    if (!isActive) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      // Check if click is inside the tooltip card
      const tooltipEl = document.querySelector('[data-tour-tooltip]');
      if (tooltipEl && tooltipEl.contains(target)) return;

      // Check if click is inside the highlighted target element
      const step = currentTour?.steps[currentStep];
      if (step?.target) {
        const highlightedEl = document.querySelector(step.target);
        if (highlightedEl && highlightedEl.contains(target)) return;
      }

      // Click was outside both — dismiss the tour
      dismissTour();
    };

    // Use mousedown so it fires before any click handlers
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isActive, currentTour, currentStep, dismissTour]);

  if (!isActive || !currentTour) {
    return null;
  }

  const step = currentTour.steps[currentStep];

  return createPortal(
    <>
      <TourSpotlight step={step} isActive={isActive} />
      <TourTooltip
        step={step}
        currentStep={currentStep}
        totalSteps={currentTour.steps.length}
        onNext={nextStep}
        onPrevious={previousStep}
        onSkip={skipTour}
        onClose={dismissTour}
      />
    </>,
    document.body
  );
}
